import React, { useCallback, useEffect, useState, forwardRef } from 'react'
import { Tab } from '../../components/Tab/Tab'
import { Card, CardProps } from './Card'
import useEmblaCarousel from 'embla-carousel-react'
import { ProgressBar } from '../../components/EmblaCarouselControls/EmblaCarouselControls'
import { EmblaCarouselType } from 'embla-carousel'
import { 
  CarouselContainer, 
  CarouselContainerInner, 
  CarouselProgress, 
  GalleryWithTabsCardDesktop, 
  GalleryWithTabsCardMobile, 
  GalleryWithTabsContainer, 
  GalleryWithTabsSection, 
  GalleryWithTabsTitle 
} from './GalleryWithTabs.styles'
import { DynamicHeadingConfig } from '../../components/DynamicHeading/DynamicHeading'
import { ActivePalette } from "../../utils/activePalette/types";
import { activeHidden, BlockVisibility } from '../common/BlockVisibility';
import { afField } from '@autodromo/contextual-fields';
import { formatGtagLabel } from '../../utils/formatGtagLabel'

export type TabProps<T> = {
  id?: string | number
  isActive: boolean
  title: string
  cards: CardProps<T>[]
}

export interface GalleryWithTabsProps<T> extends React.HTMLAttributes<HTMLDivElement>, ActivePalette, BlockVisibility {
  htmlId?: string
  title: string
  primaryColor?: string
  secondaryColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
  tabs: TabProps<T>[]
  padding?: {
    typeMeasure?: string
    desktop?: {
      paddingTop?: number
      paddingBottom?: number
    },
    mobile?: {
      paddingTop?: number
      paddingBottom?: number
    }
  },
  elementColor?: string
  preload?: boolean
  titleTag?: DynamicHeadingConfig['titleTag']
}

export const GalleryWithTabsInner = <T extends object>(
  {
    htmlId,
    title,
    primaryColor = '#000000',
    secondaryColor = '#334155',
    backgroundColor = '#F1F4F9',
    cardBackgroundColor = '#FFFFFF',
    tabs,
    padding,
    elementColor,
    titleTag = 'h2',
    activePalette,
    hideOnMobile,
    hideOnDesktop
  }: GalleryWithTabsProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const [currentTab, setCurrentTab] = useState<number>(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({ slidesToScroll: 1 })
  const [scrollProgress, setScrollProgress] = useState(0)

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 100)
  }, [])

  const activeTabs = tabs.filter((tab) => tab.isActive)

  const primaryColorByPalette = 'var(--general-primary, #334155)';

  useEffect(() => {
    if (!emblaApi) return

    onScroll(emblaApi)
    emblaApi
      .on('reInit', onScroll)
      .on('scroll', onScroll)
      .on('slideFocus', onScroll)
  }, [emblaApi, onScroll])

  return (
    <GalleryWithTabsSection
      ref={ref}
      id={htmlId}
      backgroundcolor={backgroundColor}
      data-testid='gallery-with-tabs'
      padding={padding}
      className={`gallery-with-tabs-section ${activePalette ? `${activePalette}` : ''} ${activeHidden({ hideOnMobile, hideOnDesktop })}`}
    >
      <GalleryWithTabsContainer data-anim-stagger>
        <GalleryWithTabsTitle titleTag={titleTag} color={primaryColor} data-af-field="title">{title}</GalleryWithTabsTitle>
        <Tab 
          color={primaryColor || primaryColorByPalette}
          activeColor={primaryColor || primaryColorByPalette}
          disabledColor={activePalette && 'var(--text-body)'}
          tabs={activeTabs.map((tab) => ({
            label: tab.title,
            children: <></>,
            dataset: {
              "af-field": afField('tabs', tab.id, 'title'),
              "gtag-action": 'Clique na Aba',
              "gtag-category": 'Abas',
              "gtag-label": formatGtagLabel(htmlId, tab.title),
            },
          }))}
          onChangeActive={setCurrentTab}
        />

        <GalleryWithTabsCardDesktop data-testid='gallery-with-tabs-desktop-card'>
          {activeTabs[currentTab].cards.filter((card) => card.isActive).map((card, index) => (
            <Card
              key={index}
              fieldPath={afField('tabs', activeTabs[currentTab].id, 'cards', card.id)}
              bgColor={cardBackgroundColor}
              textColor={secondaryColor}
              image={card.image}
              title={card.title}
              galleryTitle={title}
              cardIndex={index}
              link={card.link}
              openLinkInNewTab={card.openLinkInNewTab}
              htmlId={htmlId}
            />
          ))}
        </GalleryWithTabsCardDesktop>

        <GalleryWithTabsCardMobile data-testid='gallery-with-tabs-mobile-card'>
          <CarouselContainer className='embla'>
            <div className='embla__viewport' ref={emblaRef}>
              <CarouselContainerInner className='embla__container'>
                {activeTabs[currentTab].cards.filter((card) => card.isActive).map((card, index) => (
                  <Card 
                    key={index}
                    fieldPath={afField('tabs', activeTabs[currentTab].id, 'cards', card.id)}
                    className='embla__slide'
                    bgColor={cardBackgroundColor}
                    textColor={secondaryColor}
                    image={card.image}
                    title={card.title}
                    galleryTitle={title}
                    cardIndex={index}
                    link={card.link}
                    openLinkInNewTab={card.openLinkInNewTab}
                    htmlId={htmlId}
                  />
                ))}
              </CarouselContainerInner>
            </div>
          </CarouselContainer>
          <CarouselProgress>
            <ProgressBar elementColor={elementColor} scrollProgress={scrollProgress}/>
          </CarouselProgress>
        </GalleryWithTabsCardMobile>
      </GalleryWithTabsContainer>
    </GalleryWithTabsSection>
  )
}

export const GalleryWithTabs = forwardRef(GalleryWithTabsInner)
